<template>
  <v-dialog
    v-model="show"
    persistent
    max-width="500px"
    @keydown.enter="handleEnter"
    @keydown.esc="cancel()"
  >
    <v-card>
      <v-card-title>{{ atlasPicker ? 'Continue as…' : (resetPassword ? 'Forgotten Password' : title) }}</v-card-title>
      <v-card-text>
        <v-alert v-if="errMessage" type="error"><span v-html="errMessage"></span></v-alert>
        <v-alert v-if="infoMessage && !atlasPicker" type="info">{{infoMessage}}</v-alert>

        <!-- Atlas MCP: internal staff pick a client, then a user to act as -->
        <div v-if="atlasPicker">
          <!-- Step 1: choose the client workspace -->
          <div v-if="atlasStep === 'client'">
            <div class="body-2 mb-3">
              You're connecting an external app. Choose the client workspace to test in.
            </div>
            <v-progress-linear v-if="atlasLoading" indeterminate />
            <v-alert v-else-if="!atlasClients.length" type="warning" dense>
              No clients available to act in.
            </v-alert>
            <v-list v-else dense class="atlas-pick-list">
              <v-list-item
                v-for="c in atlasClients"
                :key="c.client_id"
                :disabled="atlasSubmitting"
                class="atlas-pick-item"
                @click="selectAtlasCustomer(c)"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ c.client_name }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="atlas-pick-chevron">
                  <v-icon small>mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>

          <!-- Step 2: choose the user to impersonate within that client -->
          <div v-else-if="atlasStep === 'user'">
            <div class="atlas-pick-header d-flex align-center mb-3">
              <v-btn icon small :disabled="atlasSubmitting" aria-label="Back to clients" @click="backToAtlasClients">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <div class="atlas-pick-header__text ml-1">
                <div class="caption text--secondary">Client</div>
                <div class="subtitle-2 font-weight-bold">{{ atlasSelectedClient && atlasSelectedClient.client_name }}</div>
              </div>
              <v-spacer />
              <span class="caption text--secondary">Choose a user</span>
            </div>
            <v-progress-linear v-if="atlasLoading" indeterminate />
            <v-alert v-else-if="!atlasUsers.length" type="warning" dense>
              No users available to impersonate in this client.
            </v-alert>
            <template v-else>
              <v-text-field
                v-if="atlasUsers.length > 8"
                v-model="atlasUserFilter"
                dense
                outlined
                rounded
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                placeholder="Filter users…"
                class="mb-3"
              />
              <v-list dense class="atlas-pick-list">
                <v-list-item
                  v-for="u in filteredAtlasUsers"
                  :key="u.user_id"
                  :disabled="atlasSubmitting"
                  class="atlas-pick-item"
                  @click="selectAtlasUser(u)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ u.name }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="atlas-pick-chevron">
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <v-progress-linear v-if="atlasSubmitting" indeterminate class="mt-2" />
            </template>
          </div>
        </div>

        <!-- Atlas MCP: SSO just completed — resuming the connection -->
        <div v-if="atlasResuming && !atlasPicker">
          <v-progress-linear indeterminate class="my-4" />
          <div class="body-2 text-center">Connecting…</div>
        </div>

        <template v-if="!atlasPicker && !atlasResuming">
          <!-- Atlas connector: tenant has SSO → offer it alongside password -->
          <div v-if="atlasSsoHost && !resetPassword && !setPassword" class="mb-4">
            <v-btn block color="primary" :disabled="atlasSubmitting" @click="startAtlasSso">
              Sign in with SSO
            </v-btn>
            <div class="text-center caption my-2">or sign in with your password</div>
          </div>
          <v-label v-if="resetPassword">Please enter your login id or email and press 'Request Password Reset'</v-label>
          <v-text-field
            v-model="username"
            :disabled="setPassword"
            label="Login"
            name="login"
            prepend-icon="mdi-account"
            type="text"
            ref="username"
          />
          <v-text-field
            v-if="!resetPassword"
            v-model="password"
            id="password"
            label="Password"
            name="password"
            prepend-icon="mdi-lock"
            type="password"
            ref="password"
          />
          <v-text-field
            v-if="setPassword"
            v-model="passwordconfirm"
            label="Re-enter Password"
            prepend-icon="mdi-lock"
            type="password"
          />
          <div class="d-flex justify-end">
            <v-checkbox
              v-if="!resetPassword"
              v-model="stayLoggedIn"
              label="Keep me logged in"
            />
          </div>
        </template>
      </v-card-text>
      <v-card-actions v-if="!atlasPicker && !atlasResuming">
        <v-btn
          v-if="resetPassword || setPassword"
          text
          color="primary"
          @click="cancelResetPassword()"
        >Cancel</v-btn>
        <v-btn
          v-if="!resetPassword && !setPassword"
          text
          color="primary"
          @click="resetPassword = true"
        >Forgotten Password</v-btn>
        <v-btn
          text
          small
          @click="$emit('openCookiePreferences')"
        >
          Cookies
        </v-btn>
        <v-spacer />
        <v-btn v-if="setPassword" color="primary" @click="setPasswordForToken()">Save Password</v-btn>
        <v-btn v-if="!setPassword && !resetPassword" color="primary" @click="login()">Login</v-btn>
        <v-btn
          v-if="resetPassword"
          color="primary"
          @click="requestResetPassword()"
        >Request Password Reset</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
 
<script>
import axios from "axios";
//import ResponseHandler from "@/components/ResponseHandler"; // @ is an alias to /src

export default {
  props: {
    reEnterPassword: Boolean,
    doLogin: Boolean,
    setPasswordToken: String,
    setPasswordType: String
  },
  data: function() {
    return {
      title: "",
      username: "",
      password: "",
      passwordconfirm: "",
      show: false,
      resetPassword: false,
      setPassword: false,
      errMessage: "",
      infoMessage: "",
      stayLoggedIn: false,
      ssoLogins: ['zendesk'],
      ssoRedirect: null,
      ssoReturnTo: null,
      // Atlas MCP OAuth: internal-staff impersonation picker
      atlasTxn: null,
      atlasTenantId: null,
      atlasPicker: false,
      atlasStep: 'client',        // two-step picker: 'client' → 'user'
      atlasClients: [],
      atlasSelectedClient: null,  // client chosen in step 1 (header + back)
      atlasUsers: [],             // impersonatable users in the chosen client
      atlasUserFilter: '',        // free-text filter over the user list
      atlasLoading: false,
      atlasSubmitting: false,
      atlasResuming: false,
      atlasSsoHost: null, // tenant sp_host passed by atlas-mcp → build the "Sign in with SSO" URL
      atlasDev: false
    };
  },
  watch: {
    doLogin() {
      this.init();
    },
  },
  computed: {
    // Atlas user picker: filter the impersonatable users by name (case-insensitive).
    filteredAtlasUsers() {
      const q = (this.atlasUserFilter || '').trim().toLowerCase();
      if (!q) return this.atlasUsers;
      return this.atlasUsers.filter((u) => (u.name || '').toLowerCase().includes(q));
    },
  },
  created() {
    this.checkSSORedirect();
    // Atlas MCP: SSO return (atlas_sso=1) — the IdP just established the session,
    // so resume the OAuth bridge directly rather than showing the login form.
    const atlasParams = this.atlasOAuthParams();
    if (atlasParams.get('atlas_oauth_txn') && atlasParams.get('atlas_sso') === '1') {
      this.resumeAtlasSso();
      return;
    }
    // Atlas connector login (no SSO completion yet): capture the txn + the SSO
    // entry URL (if the bound tenant has SAML) so the form can offer SSO.
    if (atlasParams.get('atlas_oauth_txn')) {
      this.atlasTxn = atlasParams.get('atlas_oauth_txn');
      this.atlasTenantId = atlasParams.get('atlas_tenant');
      this.atlasSsoHost = atlasParams.get('atlas_sso_host');
      this.atlasDev = atlasParams.get('atlas_dev') === '1';
    }
    this.init();
  },
  methods: {
    init() {
      this.show = !!this.doLogin;
      if (this.show) {
        this.setPassword = !!this.setPasswordToken;
        this.stayLoggedIn = false;
        this.title = this.setPassword
          ? (this.setPasswordType === "set" ? "Password Setup: Enter new password" : "Password Reset Request: Enter new password")
          : this.reEnterPassword
          ? "Your session has timed out. Please login."
          : "Login Details";
        this.$nextTick(() => {
          this.password = "";
          if (!this.$refs.username) return;
          if (!this.username) {
            this.$refs.username.focus;
          } else {
            this.$refs.password.focus;
          }
        });
        if (this.setPassword) {
          axios
            .post("auth/getUserForPasswordToken/", {
              password_reset_token: this.setPasswordToken,
            })
            .then(resp => {
              if (resp.data.Status === "OK") {
                this.username = resp.data.Data;
              } else {
                this.errMessage = resp.data.Message;
                this.setPassword = false;
                this.title = "Login Details";
              }
            })
            .catch(err => {
              this.errMessage = err;
              console.log(err);
            });
        }
      }
    },
    handleEnter() {
      if (this.setPassword)
        this.setPasswordForToken();
      else if (this.resetPassword)
        this.requestResetPassword();
      else
        this.login();
    },
    checkSSORedirect() {            
      // on login we need to sniff for redirect from zendesk support in event they hit an article or ticket with no log in
      const params = new URLSearchParams(window.location.search);
      const returnTo = params.get('return_to');

      // have we come in on a zendesk redirect for login? if so we need to log and clear for UX or we get bad history
      if (returnTo && /^https?:\/\/(?:support.role-mapper.com|.*.supp.zendesk.com)/.test(returnTo)) {
        this.ssoReturnTo = returnTo;
        this.ssoRedirect = 'zendesk';
        window.history.replaceState({}, '', `/`);
      }
    },
    // Atlas MCP SSO initiation: redirect to the tenant's SAML entry (sp_eid),
    // carrying the OAuth txn (+ staff flag) through RelayState. After auth the
    // ACS bounces back to the portal with atlas_sso=1 and resumeAtlasSso runs.
    startAtlasSso() {
      if (!this.atlasSsoHost || !this.atlasTxn) return;
      const dev = this.atlasDev ? '&atlas_dev=1' : '';
      // Build the SSO entry URL from OUR rm-api origin (the base the portal
      // already calls) + the tenant's sp_host. This is a top-level navigation,
      // so the SSO URL never appears in the portal's query string.
      let origin;
      try {
        origin = new URL(process.env.VUE_APP_API_BASE_URL).origin;
      } catch (e) {
        origin = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '');
      }
      window.location.assign(
        `${origin}/sso/${encodeURIComponent(this.atlasSsoHost)}?atlas_oauth_txn=${encodeURIComponent(this.atlasTxn)}${dev}`,
      );
    },
    // Atlas MCP SSO return: the IdP set the RMAPI_RT cookie, so resume the OAuth
    // bridge without a login form. Read the session to learn if this is an
    // internal (staff) user — they pick a customer to act in; customer users
    // complete the exchange directly.
    resumeAtlasSso() {
      const params = this.atlasOAuthParams();
      const txn = params.get('atlas_oauth_txn');
      if (!txn) { this.init(); return; }
      this.atlasTxn = txn;
      this.atlasTenantId = params.get('atlas_tenant');
      this.atlasResuming = true;
      this.show = true; // init() is skipped on resume; show the dialog ourselves
      axios
        .post('auth/loginstate/', { impersonatingUserIndex: 0 })
        .then((resp) => {
          const internal = resp.data && resp.data.Status === 'OK'
            && resp.data.user && resp.data.user.client_type === 'INTERNAL';
          if (internal) {
            this.loadAtlasCustomers();
            this.atlasPicker = true;
          } else {
            this.completeAtlasOAuth();
          }
        })
        .catch(() => {
          this.atlasResuming = false;
          this.errMessage = 'Could not resume the connection. Please log in.';
          this.init();
        });
    },
    login() {
      const un = this.username.trim(),
        pw = this.password.trim();
      if (un.length === 0 || pw.length === 0) {
        this.errMessage = "User Name and Password must be entered";
        return;
      }
      
      this.errMessage = "";
      this.infoMessage = "";

      axios
        .post("auth/login/", {
          username: un,
          password: pw,
          stayLoggedIn: this.stayLoggedIn
        })
        .then(resp => {
          if (resp.data.Status === "OK") {
            this.errMessage = "";
            this.password = "";

            // Atlas MCP OAuth bridge: if an external MCP host (Claude Desktop)
            // started OAuth, atlas-mcp-app bounced here with ?atlas_oauth_txn=
            // (+ ?atlas_tenant= for the connector token's tenant).
            const params = this.atlasOAuthParams();
            const atlasTxn = params.get('atlas_oauth_txn');
            if (atlasTxn) {
              this.atlasTxn = atlasTxn;
              this.atlasTenantId = params.get('atlas_tenant');
              const isInternal = resp.data.user && resp.data.user.client_type === 'INTERNAL';
              if (isInternal) {
                // Internal staff: pick the customer to act in (its mapped
                // internal_user is the impersonation target). Keep the modal
                // open and do NOT emit LoggedIn — that would route App.vue into
                // its own customer selection. completeAtlasOAuth fires on select.
                this.loadAtlasCustomers();
                this.atlasPicker = true;
                return;
              }
              // Customer user: complete directly and bounce to the MCP origin.
              this.completeAtlasOAuth();
              return;
            }

            this.show = false;
            this.$emit("LoggedIn", resp.data);

            // is this a zendesk redirect return url? did it come from zendesk?
            if (this.ssoLogins.includes(this.ssoRedirect)) {
              // ask for sso link from url
              axios
                .post('auth/ssolink', { link: { url: this.ssoReturnTo, sso: this.ssoRedirect}})
                .then((resp) => {
                  if (!resp.data || !resp.data.url) return  alert('Error Creating SSO Link, cannot log you in to alternative resource');
                  // clear redirects and redirect using sso link
                  this.ssoReturnTo = null;
                  this.ssoRedirect = null;
                  window.open(resp.data.url, '_self');
                })
                .catch(() => {
                    alert('Error Creating SSO Link, cannot log you in to alternative resource');
                });
            }
          } else {
            this.errMessage = "Username or Password are incorrect";
            console.log(resp.data.Message);
          }
          //console.log(resp);
        })
        .catch(err => {
          //this.loginState.isAuthenticated = false;
          this.errMessage = "Username or Password are incorrect";
          console.log(err);
        });
    },
    cancel() {
      this.password = "";
      this.$emit("Cancelled");
    },
    // Read the atlas_oauth_txn / atlas_tenant params. rm-portal is hash-routed
    // (/#/login?…), so the query lands in the hash fragment and
    // window.location.search is empty — fall back to parsing the hash.
    atlasOAuthParams() {
      let search = window.location.search;
      if (!search || search.indexOf('atlas_oauth_txn') === -1) {
        const hash = window.location.hash || '';
        const qIndex = hash.indexOf('?');
        if (qIndex >= 0) search = hash.substring(qIndex);
      }
      return new URLSearchParams(search);
    },
    // Atlas MCP step 1: load the clients the internal/staff user can act in.
    // Tenant-bound connector → just that one (auto-advances to the user picker);
    // dev unbound (no atlas_tenant) → all clients, so the user can pick which to
    // test in.
    loadAtlasCustomers() {
      this.atlasStep = 'client';
      this.atlasLoading = true;
      axios
        .get('client/customerList')
        .then((r) => {
          let list = (r.data && r.data.Data) || (Array.isArray(r.data) ? r.data : []);
          if (this.atlasTenantId) {
            list = list.filter((c) => String(c.client_id) === String(this.atlasTenantId));
          }
          this.atlasClients = list;
        })
        .catch(() => { this.atlasClients = []; })
        .finally(() => {
          this.atlasLoading = false;
          // A tenant-bound connector resolves to a single client — skip the
          // (redundant) client step and go straight to the user picker.
          if (this.atlasClients.length === 1) {
            this.selectAtlasCustomer(this.atlasClients[0]);
          }
        });
    },
    // Step 1 → 2: remember the chosen client and load its impersonatable users.
    selectAtlasCustomer(c) {
      if (!c) return;
      this.atlasSelectedClient = c;
      this.atlasUsers = [];
      this.atlasUserFilter = '';
      this.atlasStep = 'user';
      this.loadAtlasUsers(c.client_id);
    },
    // Atlas MCP step 2: load the users in the chosen client this login may
    // impersonate. getImpersonationUsers enforces source-is-INTERNAL / the
    // login's impersonation_rights server-side, and the /api/oauth/exchange
    // re-validates the picked user — so picking any of these lets us exercise
    // every permission group within the client.
    loadAtlasUsers(clientId) {
      this.atlasLoading = true;
      axios
        .get(`user/impersonationUsers/${encodeURIComponent(clientId)}`)
        .then((r) => {
          this.atlasUsers = (r.data && r.data.Data) || (Array.isArray(r.data) ? r.data : []);
        })
        .catch(() => { this.atlasUsers = []; })
        .finally(() => { this.atlasLoading = false; });
    },
    // Step 2 → done: impersonate the selected user and complete the exchange.
    selectAtlasUser(u) {
      if (!u || !u.user_id) return;
      this.completeAtlasOAuth(u.user_id);
    },
    // Back to the client list (clears the in-flight user selection).
    backToAtlasClients() {
      this.atlasStep = 'client';
      this.atlasSelectedClient = null;
      this.atlasUsers = [];
      this.atlasUserFilter = '';
    },
    // Exchange the in-flight OAuth txn (cookie-scoped) for a one-time token and
    // bounce to the MCP resume URL. impersonateUserId is set only for the
    // internal-staff picker; customer users call with no argument.
    completeAtlasOAuth(impersonateUserId) {
      if (!this.atlasTxn) return;
      this.atlasSubmitting = true;
      const body = { txn_id: this.atlasTxn };
      if (impersonateUserId) body.impersonate_user_id = impersonateUserId;
      axios
        .post('api/oauth/exchange', body)
        .then((r) => {
          if (r.data && r.data.Status === 'OK' && r.data.resume_url && r.data.ott) {
            window.location.assign(`${r.data.resume_url}?ott=${encodeURIComponent(r.data.ott)}`);
          } else {
            this.atlasSubmitting = false;
            this.errMessage = 'Could not complete the connection. Please try again.';
          }
        })
        .catch(() => {
          this.atlasSubmitting = false;
          this.errMessage = 'Could not complete the connection. Please try again.';
        });
    },
    cancelResetPassword() {
      this.resetPassword = false;
      this.setPassword = false;
      this.errMessage = "";
    },
    requestResetPassword() {
      const un = this.username.trim();
      if (un.length === 0) {
        this.errMessage = "User Login must be entered";
        return;
      }
      this.errMessage = "";
      this.infoMessage = "";

      axios
        .post("auth/resetPasswordRequest/", { username: un })
        .then(resp => {
          if (resp.data.Status === "OK") {
        //    this.show = false;
            this.password = "";
			this.infoMessage = resp.data.Message;
			this.resetPassword = false;
			this.setPassword = false;
          } else {
            this.errMessage = resp.data.Message;
          }
          //console.log(resp);
        })
        .catch(err => {
          //this.loginState.isAuthenticated = false;
          this.errMessage = err;
          console.log(err);
        });
    },
    setPasswordForToken() {
      const pw = this.password.trim(),
        pwc = this.passwordconfirm,
        hasUpperCase = /[A-Z]/.test(pw),
        hasLowerCase = /[a-z]/.test(pw),
        hasNumbers = /\d/.test(pw),
        hasNonalphas = /[^a-zA-Z\d\s:]/.test(pw);
      if (pw.length < 8 || !hasUpperCase || !hasLowerCase || !hasNumbers || !hasNonalphas) {
        this.errMessage = `Invalid Password
          <ul>
            <li>Must be at least 8 characters long</li>
            <li>Must have at least one uppercase character</li>
            <li>Must have at least one lowercase character</li>
            <li>Must have at least one numeric value</li>
            <li>Must have at least one special character</li>
          </ul>`;
        return;
      }
      if (pw !== pwc) {
        this.errMessage = "Passwords do not match";
        return;
      }
      this.errMessage = "";
      this.infoMessage = "";

      axios
        .post("auth/setPasswordForToken/", {
          password_reset_token: this.setPasswordToken,
          password: pw,
          stayLoggedIn: this.stayLoggedIn
        })
        .then(resp => {
          if (resp.data.Status === "OK") {
            this.show = false;
            this.resetPassword = false;
            this.setPassword = false;
            this.password = "";
            location.hash = "#/home";
            this.$emit("LoggedIn", resp.data);
          } else {
            this.errMessage = resp.data.Message;
          }
          //console.log(resp);
        })
        .catch(err => {
          //this.loginState.isAuthenticated = false;
          this.errMessage = err;
          console.log(err);
        });
    }
  }
};
</script>

<style scoped>
/* Atlas MCP picker — professional client/user selection UI */

/* Cap long lists so the dialog scrolls internally, with breathing room. */
.atlas-pick-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 2px;
}

/* Each client/user row reads as a rounded, bordered card. */
.atlas-pick-item {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  margin-bottom: 8px;
  min-height: 48px;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}
.atlas-pick-item:last-child {
  margin-bottom: 0;
}
.atlas-pick-item:hover {
  border-color: var(--v-primary-base, #1976d2);
  background-color: rgba(25, 118, 210, 0.05);
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.09);
}

/* Chevron stays quiet until the row is hovered. */
.atlas-pick-chevron {
  opacity: 0.35;
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.atlas-pick-item:hover .atlas-pick-chevron {
  opacity: 1;
  transform: translateX(2px);
}

/* Step-2 header: the chosen client shown as a soft banner. */
.atlas-pick-header {
  background-color: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 8px 12px;
}
.atlas-pick-header__text {
  line-height: 1.2;
}
</style>